import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type PaginationState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { format, parseISO, subMonths } from 'date-fns';
import { ChevronDown, ChevronUp, Copy, Filter, Info } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  type PaymentTransaction,
  useGetPaymentTransactionList,
} from '~entities/payment/api/get-payment-transaction-list';

import { DateRangePicker } from '~features/payment/ui/date-range-picker';

import { useGetCurrency } from '~shared/api/currency-api';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { normalizePayments } from '~shared/helpers/normalize-payments';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { formatPaymentMethodSummary } from '~shared/helpers/payment-method-summary';
import useDebounce from '~shared/hooks/useDebounce';
import { Button } from '~shared/shadcn/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~shared/shadcn/ui/form';
import { Input } from '~shared/shadcn/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

enum InvoiceStatus {
  CANCELLED = 'cancelled',
  FAILED = 'failed',
  FULL_REFUND = 'full refund',
  PAID = 'paid',
  PARTIAL_REFUND = 'partial refund',
  PARTIALLY_PAID = 'partially paid',
  PAUSED = 'paused',
  PENDING = 'pending',
  PENDING_REFUND = 'refund processing',
  PROCESSING = 'processing',
  REFUND_FAILED = 'refund failed',
  SCHEDULED = 'scheduled',
  UPCOMING = 'upcoming',
  VOID = 'void',
}

const filterSchema = z.object({
  studentName: z.string().optional(),
  classChargeName: z.string().optional(),
  transactionCode: z.string().optional(),
  status: z.string().optional(),
});

type FilterFormValues = z.infer<typeof filterSchema>;

const columnHelper = createColumnHelper<PaymentTransaction>();

export const StudioTransactionTable = () => {
  const { data: currencyDetails } = useGetCurrency();

  const columns = useMemo(
    () => [
      columnHelper.accessor('invoiceDate', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Invoice Date'
          />
        ),
        cell: (info) => (
          <div className='px-4'>
            {info.getValue().toString()
              ? format(parseISO(info.getValue().toString()), 'MMMM dd, yyyy')
              : ''}
          </div>
        ),
      }),
      columnHelper.accessor('paidDate', {
        header: (info) => (
          <DataTableColumnHeader
            className='w-36 pl-4 text-black'
            column={info.column}
            title='Paid Date'
          />
        ),
        cell: (info) => (
          <div className='px-4'>
            {info.getValue()?.toString()
              ? format(
                  parseISO(info.getValue()?.toString() ?? ''),
                  'MMMM dd, yyyy',
                )
              : ''}
          </div>
        ),
      }),
      columnHelper.accessor('classChargeName', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Class/Charge Name'
          />
        ),
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('parentName', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Parent Name'
          />
        ),
        cell: (info) => (
          <Button
            variant={'link'}
            className='capitalize text-custom-secondary-1'
            asChild
          >
            <Link
              to={`/family`}
              search={{ parentId: info.row.original.parentId }}
            >
              {info.getValue()}
            </Link>
          </Button>
        ),
      }),
      columnHelper.accessor('studentName', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Student Name'
          />
        ),
        cell: (info) => (
          <Button
            variant={'link'}
            className='capitalize text-custom-secondary-1'
            asChild
          >
            <Link
              to={`/students/$studentId/details`}
              params={{ studentId: info.row.original.studentId }}
            >
              {info.getValue()}
            </Link>
          </Button>
        ),
      }),
      columnHelper.accessor('fundsAvailableOn', {
        header: (info) => (
          <DataTableColumnHeader
            className='w-40 pl-4 text-black'
            column={info.column}
            title='Funds available by'
          />
        ),
        cell: (info) => {
          const row = info.row.original;
          const isPaid = row.status.toLowerCase() === 'paid';
          const value = info.getValue();
          const hasDate = isPaid && value;
          if (!hasDate) {
            return <div className='px-4'>—</div>;
          }
          const display = format(parseISO(value), 'MMM d, yyyy');
          return (
            <div className='flex items-center gap-1.5 px-4'>
              <span>{display}</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info
                    className='size-3.5 shrink-0 text-muted-foreground'
                    aria-label='Funds available estimate info'
                  />
                </TooltipTrigger>
                <TooltipContent side='top' className='max-w-xs'>
                  <p>
                    Estimated by Stripe. Actual availability may vary and is
                    determined by Stripe and your bank.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          );
        },
      }),
      columnHelper.accessor('payments', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Transaction ID'
          />
        ),
        cell: (info) => {
          const payments = normalizePayments(info.getValue());
          const transactionIds = payments
            .map((payment) => payment.paymentIntentId)
            .filter(Boolean);
          const textToCopy = transactionIds.join(', ');
          const handleCopy = async () => {
            if (!textToCopy) return;
            try {
              await navigator.clipboard.writeText(textToCopy);
              toast.success('Transaction ID copied to clipboard');
            } catch {
              toast.error('Failed to copy');
            }
          };
          return (
            <div className='flex w-full min-w-0 items-center justify-between gap-2 px-4'>
              <span className='min-w-0 truncate'>
                {transactionIds[0] ?? '—'}
              </span>
              {transactionIds.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type='button'
                      variant='ghost'
                      size='icon'
                      className='size-7 shrink-0'
                      onClick={handleCopy}
                      aria-label='Copy transaction ID'
                    >
                      <Copy className='size-3.5' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Copy transaction ID</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor('transactionCode', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Transaction Code'
          />
        ),
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('status', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Status'
          />
        ),
        cell: (info) => (
          <div className='px-4 capitalize'>{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor('invoiceValue', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Invoice Value'
          />
        ),
        cell: (info) => (
          <div className='px-4'>
            {currencyDetails?.symbol}
            {info.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor('discountAdjustment', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Discount / Adjustment'
          />
        ),
        cell: (info) => (
          <div className='px-4'>
            {currencyDetails?.symbol}
            {info.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor('paidAmount', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Paid'
          />
        ),
        cell: (info) => (
          <div className='px-4'>
            {currencyDetails?.symbol}
            {info.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor('payments', {
        id: 'paymentType',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Payment Type '
          />
        ),
        cell: (info) => (
          <div className='px-4 py-2'>
            {formatPaymentMethodSummary(info.getValue()) || '—'}
          </div>
        ),
      }),
      columnHelper.accessor('refundAmount', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Refund Amount'
          />
        ),
        cell: (info) => (
          <div className='px-4'>
            {currencyDetails?.symbol}
            {info.getValue()}
          </div>
        ),
      }),
    ],
    [currencyDetails?.symbol],
  );

  const today = new Date();
  const defaultStart = subMonths(today, 3);
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: defaultStart,
    to: today,
  });
  const startDate = dateRange?.from
    ? format(dateRange.from, 'yyyy-MM-dd')
    : undefined;
  const endDate = dateRange?.to
    ? format(dateRange.to, 'yyyy-MM-dd')
    : undefined;

  const [searchQuery, setSearchQuery] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterFormValues>({
    studentName: '',
    classChargeName: '',
    transactionCode: '',
    status: '',
  });

  const form = useForm<FilterFormValues>({
    resolver: zodResolver(filterSchema),
    defaultValues: {
      studentName: '',
      classChargeName: '',
      transactionCode: '',
      status: '',
    },
  });

  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const debouncedStudentName = useDebounce(filters.studentName, 500);
  const debouncedClassChargeName = useDebounce(filters.classChargeName, 500);
  const debouncedTransactionCode = useDebounce(filters.transactionCode, 500);
  const debouncedStatus = useDebounce(filters.status, 500);

  const { data, isLoading } = useGetPaymentTransactionList({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    startDate,
    endDate,
    parentName: debouncedSearchQuery,
    studentName: debouncedStudentName,
    classChargeName: debouncedClassChargeName,
    transactionCode: debouncedTransactionCode,
    status: debouncedStatus,
  });

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const onSubmit = (data: FilterFormValues) => {
    setFilters(data);
  };

  const onClear = () => {
    form.reset({
      studentName: '',
      classChargeName: '',
      transactionCode: '',
      status: '',
    });
  };

  return (
    <EnrollioTable
      ariaLabel='Studio transactions'
      totalLabel='Transactions'
      filterHeader={
        <>
          <EnrollioTableSearch
            placeholder='Search parents...'
            value={searchQuery}
            onChange={setSearchQuery}
          />
          <DateRangePicker date={dateRange} setDate={setDateRange} />
          <Button
            onClick={toggleFilters}
            variant='outline'
            className='flex items-center gap-2'
          >
            <Filter className='size-4' />
            Show Filters
            {showFilters ? (
              <ChevronUp className='size-4' />
            ) : (
              <ChevronDown className='size-4' />
            )}
          </Button>
          {showFilters ? (
            <div className='w-full space-y-4 rounded-lg border p-6 duration-200 animate-in slide-in-from-top-2'>
              <h3 className='mb-4 text-lg font-semibold'>Filter Options</h3>

              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className='space-y-4'
                >
                  <div className='flex flex-col items-end gap-4 sm:flex-row'>
                    <FormField
                      control={form.control}
                      name='studentName'
                      render={({ field }) => (
                        <FormItem className='flex-1'>
                          <FormLabel>Student Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder='Enter student name'
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='classChargeName'
                      render={({ field }) => (
                        <FormItem className='flex-1'>
                          <FormLabel>Class/Charge Name</FormLabel>
                          <FormControl>
                            <Input
                              type='text'
                              placeholder='Enter class/charge name'
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='transactionCode'
                      render={({ field }) => (
                        <FormItem className='flex-1'>
                          <FormLabel>Transaction Code</FormLabel>
                          <FormControl>
                            <Input
                              type='text'
                              placeholder='Enter transaction code'
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='status'
                      render={({ field }) => (
                        <FormItem className='flex-1'>
                          <FormLabel>Status</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder='Select category' />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {Object.entries(InvoiceStatus).map(
                                ([key, value]) => (
                                  <SelectItem key={key} value={value}>
                                    {value
                                      .split(' ')
                                      .map(
                                        (word) =>
                                          word.charAt(0).toUpperCase() +
                                          word.slice(1),
                                      )
                                      .join(' ')}
                                  </SelectItem>
                                ),
                              )}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className='flex gap-2 sm:flex-col lg:flex-row'>
                      <Button type='submit' size='sm'>
                        Apply
                      </Button>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={onClear}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                </form>
              </Form>
            </div>
          ) : null}
        </>
      }
      toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
      columnFilters={columnFilters}
      columnVisibility={columnVisibility}
      columns={columns}
      data={data?.data ?? []}
      isUpdating={isLoading}
      onColumnFiltersChange={setColumnFilters}
      onColumnVisibilityChange={setColumnVisibility}
      onSortingChange={setSorting}
      sorting={sorting}
      pagination={pagination}
      onPaginationChange={setPagination}
      manualPagination
      rowCount={data?.total ?? 0}
    />
  );
};
